import { Button } from '@thanx/uikit/button'
import { Select } from '@thanx/uikit/select'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import {
  ChannelValue,
  useAssignCaptureRateChannelMutation,
  useBulkAssignCaptureRateChannelsMutation,
} from 'api/captureRate'
import { getErrorMessage } from 'api/utils'
import ExclamationTriangleO from 'components/Icons/ExclamationTriangleO'
import ProviderLogo from 'components/ProviderLogo'
import { posProviderLogoKey } from 'components/ProviderLogo/posProviderLogoKey'
import SortableColumnHeader, {
  SortDirection,
} from 'components/SortableColumnHeader'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAbility } from 'utilities/ability'
import { currencyFromCents, percentageFormatter } from 'utilities/formatters'
import ConfirmChannelModal from './ConfirmChannelModal'
import { isConfirmable, RevenueSourceRow } from './revenueSourceGrouping'

const t = buildTranslate('capture_rate.revenue_sources.table')
const tErrors = buildTranslate('capture_rate.revenue_sources.errors')
const COLUMN_COUNT = 7
// Mirrors CaptureRate::BulkAssignChannels::MAX_ROWS (thanx/nexus) -- the
// backend rejects a larger batch outright, so the button must never send one.
const BULK_ASSIGN_MAX_ROWS = 100

const CHANNEL_VALUES: ChannelValue[] = ['instore', 'digital', 'third_party']

function isChannelValue(value: string | undefined): value is ChannelValue {
  return (CHANNEL_VALUES as string[]).includes(value ?? '')
}

function channelOptions() {
  return CHANNEL_VALUES.map(value => ({
    value,
    label: t(`channel_value.${value}`),
  }))
}

type SortColumn =
  | 'revenue_source'
  | 'pos'
  | 'channel'
  | 'total_revenue'
  | 'percent_of_revenue'
  | 'transactions'

function channelLabel(row: RevenueSourceRow): string {
  if (row.noChannel) return t('no_channel_data')
  if (row.value) return t(`channel_value.${row.value}`)
  return t('needs_review')
}

// Falls back whenever the label itself is missing (e.g. a $0 zero-filled
// mapping with no raw name), not only for noChannel rows.
function revenueSourceLabel(row: RevenueSourceRow): string {
  return row.providerLabel || t('no_revenue_source')
}

function sortKeyFor(
  column: SortColumn,
  row: RevenueSourceRow
): string | number {
  switch (column) {
    case 'revenue_source':
      return revenueSourceLabel(row)
    case 'pos':
      return t(`provider.${row.provider}`)
    case 'channel':
      return channelLabel(row)
    case 'total_revenue':
      return row.totalCents
    case 'percent_of_revenue':
      return row.percentOfRevenue
    case 'transactions':
      return row.orderCount
  }
}

function compareRows(
  column: SortColumn,
  direction: 'asc' | 'desc'
): (a: RevenueSourceRow, b: RevenueSourceRow) => number {
  const ascending = (a: RevenueSourceRow, b: RevenueSourceRow) => {
    const aKey = sortKeyFor(column, a)
    const bKey = sortKeyFor(column, b)
    return typeof aKey === 'string'
      ? aKey.localeCompare(bKey as string)
      : (aKey as number) - (bKey as number)
  }
  return direction === 'asc' ? ascending : (a, b) => ascending(b, a)
}

function SectionRow({
  id,
  label,
  highlighted = false,
  action,
  headingRef,
}: {
  id: string
  label: string
  highlighted?: boolean
  action?: React.ReactNode
  headingRef?: React.RefObject<HTMLSpanElement>
}) {
  const [css] = useStyletron()
  const rowClass = css({
    backgroundColor: highlighted ? palette.eggYolk10 : undefined,
  })
  return (
    <tr key={id} className={rowClass}>
      <td colSpan={COLUMN_COUNT} className="py-s px-m">
        <div className={css({ position: 'relative' })}>
          <span
            ref={headingRef}
            tabIndex={-1}
            className={css({ outline: 'none' })}
          >
            <Text.BodyText5
              bold
              color="grey70"
              className={css({
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              })}
            >
              {label}
            </Text.BodyText5>
          </span>
          {action && (
            // Absolutely positioned so it doesn't count toward this
            // colSpan cell's intrinsic width -- otherwise the table's
            // auto layout stretches the (unrelated) last column to fit it.
            <div
              className={css({
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
              })}
            >
              {action}
            </div>
          )}
        </div>
      </td>
    </tr>
  )
}

type RowProps = {
  row: RevenueSourceRow
  cellClass: string
  canManage: boolean
  disabled: boolean
  onLoadingChange: (rowId: string, isLoading: boolean) => void
}

function reviewCaption(row: RevenueSourceRow): string {
  return row.value != null ? t('suggested_caption') : t('no_suggestion_caption')
}

function SourceRow({
  row,
  cellClass,
  canManage,
  disabled,
  onLoadingChange,
}: RowProps) {
  const [css] = useStyletron()
  // Manual rows show no caption -- no per-user attribution exists, so
  // "Edited by you" would be a false claim.
  const sourceCaption = row.needsReview
    ? reviewCaption(row)
    : row.source && row.source !== 'manual' && t(`source.${row.source}`)
  const rowClass = css({
    backgroundColor: row.needsReview ? palette.eggYolk10 : undefined,
  })
  // One picked-but-unconfirmed value, tagged with whether it needs the
  // confirm modal -- keeps the two states from being set independently.
  const [draft, setDraft] = useState<{
    value: ChannelValue
    needsConfirm: boolean
  } | null>(null)
  const [assignChannel, { isLoading, error, reset }] =
    useAssignCaptureRateChannelMutation()

  // Lets the bulk button know this row has its own mutation in flight, so
  // it can't be submitted a second time via the bulk endpoint concurrently.
  useEffect(() => {
    onLoadingChange(row.id, isLoading)
    return () => onLoadingChange(row.id, false)
  }, [row.id, isLoading, onLoadingChange])

  const editable = canManage && !row.noChannel
  const selectedValue = draft && !draft.needsConfirm ? draft.value : row.value
  // Same 3 options and translate() calls on every row -- memoized so a
  // re-render of one row (e.g. its own mutation's isLoading flipping)
  // doesn't rebuild them for the other ~300-550 rows' worth of renders.
  const options = useMemo(channelOptions, [])
  const suggestionBorderOverrides = useMemo(
    () =>
      // Flags a not-yet-confirmed suggestion -- the no-suggestion
      // (placeholder) and already-mapped cases keep the default border.
      row.needsReview && row.value != null
        ? {
            ControlContainer: {
              style: {
                borderColor: palette.orange,
                borderWidth: '1.5px',
              },
            },
          }
        : undefined,
    [row.needsReview, row.value]
  )

  function submit(value: ChannelValue) {
    assignChannel({
      provider: row.provider,
      providerKey: row.providerKey,
      value,
    })
      .unwrap()
      .then(() => {
        setDraft(null)
      })
      .catch(() => {
        // error renders inline below via the mutation's error state
      })
  }

  function handleChannelChange(value: ChannelValue) {
    reset()
    setDraft({ value, needsConfirm: row.value != null && value !== row.value })
  }

  function handleConfirm() {
    reset()
    if (selectedValue) submit(selectedValue)
  }

  const canConfirm =
    !draft?.needsConfirm &&
    isConfirmable({ needsReview: row.needsReview, value: selectedValue })

  return (
    <>
      <tr className={rowClass}>
        <td className={`px-m align-middle ${cellClass}`}>
          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            })}
          >
            {row.needsReview && (
              <span aria-hidden="true">
                <ExclamationTriangleO pathColor={palette.eggYolk} />
              </span>
            )}
            {row.providerLabel ? (
              <Text.BodyText4 bold>{row.providerLabel}</Text.BodyText4>
            ) : (
              <Text.BodyText4 color="grey70">
                {t('no_revenue_source')}
              </Text.BodyText4>
            )}
          </div>
        </td>
        <td className={`px-m align-middle ${cellClass}`}>
          <ProviderLogo
            logoKey={posProviderLogoKey(row.provider)}
            label={t(`provider.${row.provider}`)}
            iconOnly
          />
        </td>
        <td className={`px-m align-middle ${cellClass}`}>
          {editable ? (
            <div className={css({ minWidth: '180px' })}>
              <Select
                aria-label={t('channel_for', {
                  rawChannel: revenueSourceLabel(row),
                  pos: t(`provider.${row.provider}`),
                })}
                placeholder={t('select_channel_placeholder')}
                value={
                  selectedValue
                    ? [
                        {
                          value: selectedValue,
                          label: t(`channel_value.${selectedValue}`),
                        },
                      ]
                    : []
                }
                options={options}
                searchable={false}
                clearable={false}
                disabled={isLoading || disabled}
                onChange={selected => {
                  const value = selected[0]?.value
                  if (isChannelValue(value)) handleChannelChange(value)
                }}
                overrides={suggestionBorderOverrides}
              />
            </div>
          ) : row.noChannel ? (
            <Text.BodyText4 color="grey70">{channelLabel(row)}</Text.BodyText4>
          ) : row.value ? (
            channelLabel(row)
          ) : (
            <Text.BodyText4 color="negative">
              {channelLabel(row)}
            </Text.BodyText4>
          )}
          {sourceCaption && (
            <Text.BodyText6
              color={row.needsReview ? 'warning' : 'grey50'}
              tag="div"
            >
              {sourceCaption}
            </Text.BodyText6>
          )}
          {error && (
            <div role="alert">
              <Text.BodyText6 color="negative" tag="div">
                {getErrorMessage(error, tErrors('assign_channel'))}
              </Text.BodyText6>
            </div>
          )}
        </td>
        <td className={`px-m align-middle text-right ${cellClass}`}>
          <Text.BodyText4 color="grey70">
            {currencyFromCents(row.totalCents)}
          </Text.BodyText4>
        </td>
        <td className={`px-m align-middle text-right ${cellClass}`}>
          <Text.BodyText4 color="grey70">
            {percentageFormatter(row.percentOfRevenue)}
          </Text.BodyText4>
        </td>
        <td className={`px-m align-middle text-right ${cellClass}`}>
          <Text.BodyText4 color="grey70">{row.orderCount}</Text.BodyText4>
        </td>
        <td className={`px-m align-middle text-right ${cellClass}`}>
          {editable && row.needsReview && (
            <Button
              kind="minimal"
              size="small"
              className={css({ color: palette.belizeHole })}
              disabled={!canConfirm || isLoading || disabled}
              isLoading={isLoading}
              onClick={handleConfirm}
              aria-label={t('confirm_channel_for', {
                rawChannel: revenueSourceLabel(row),
              })}
            >
              {t('confirm')}
            </Button>
          )}
        </td>
      </tr>
      {draft?.needsConfirm && row.value && (
        <ConfirmChannelModal
          isOpen
          isLoading={isLoading}
          errorMessage={
            error
              ? getErrorMessage(error, tErrors('assign_channel'))
              : undefined
          }
          rawChannel={revenueSourceLabel(row)}
          fromValue={row.value}
          toValue={draft.value}
          totalCents={row.totalCents}
          orderCount={row.orderCount}
          onKeep={() => {
            reset()
            setDraft(null)
          }}
          onChange={() => submit(draft.value)}
        />
      )}
    </>
  )
}

type Props = {
  rows: RevenueSourceRow[]
}

const RevenueSourcesTable = ({ rows }: Props) => {
  const [css] = useStyletron()
  const ability = useAbility()
  const canManage = ability.can('manage', 'LocationReports')
  const cellClass = css({
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: palette.grey30,
    paddingTop: '20px',
    paddingBottom: '20px',
  })

  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>(null)

  function handleSort(column: SortColumn) {
    if (sortColumn !== column) {
      setSortColumn(column)
      setSortDirection('asc')
      return
    }
    setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
  }

  const { needsReview, mapped, noChannelRows } = useMemo(() => {
    function applySort(sectionRows: RevenueSourceRow[]): RevenueSourceRow[] {
      return sortColumn && sortDirection
        ? [...sectionRows].sort(compareRows(sortColumn, sortDirection))
        : sectionRows
    }

    return {
      needsReview: applySort(rows.filter(row => row.needsReview)),
      mapped: applySort(rows.filter(row => !row.needsReview && !row.noChannel)),
      noChannelRows: applySort(rows.filter(row => row.noChannel)),
    }
  }, [rows, sortColumn, sortDirection])

  // A confirmed row's Confirm button (or the bulk button, once the count
  // hits 0) unmounts, dropping focus to <body> with no feedback for a
  // keyboard/screen-reader user. Move it to the section heading instead,
  // but only when the count actually drops -- not on every render. When the
  // last needs-review row is confirmed, that heading unmounts too (the
  // section itself goes away), so fall back to the table's own ref.
  const needsReviewHeadingRef = useRef<HTMLSpanElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const prevNeedsReviewCountRef = useRef(needsReview.length)
  useEffect(() => {
    if (needsReview.length < prevNeedsReviewCountRef.current) {
      if (needsReviewHeadingRef.current) {
        needsReviewHeadingRef.current.focus()
      } else {
        tableRef.current?.focus()
      }
    }
    prevNeedsReviewCountRef.current = needsReview.length
  }, [needsReview.length])

  const confirmableRows = needsReview.filter(isConfirmable)
  const bulkRows = confirmableRows.slice(0, BULK_ASSIGN_MAX_ROWS)
  const [bulkAssign, { isLoading: isBulkLoading, error: bulkError }] =
    useBulkAssignCaptureRateChannelsMutation()

  // Tracks rows with their own in-flight mutation so the bulk button can't
  // submit the same row a second time while it's still pending.
  const [pendingRowIds, setPendingRowIds] = useState<Set<string>>(new Set())
  const handleRowLoadingChange = useCallback(
    (rowId: string, isRowLoading: boolean) => {
      setPendingRowIds(prev => {
        const alreadyPending = prev.has(rowId)
        if (isRowLoading === alreadyPending) return prev
        const next = new Set(prev)
        if (isRowLoading) {
          next.add(rowId)
        } else {
          next.delete(rowId)
        }
        return next
      })
    },
    []
  )
  const bulkDisabled = isBulkLoading || pendingRowIds.size > 0

  function handleBulkConfirm() {
    if (bulkRows.length === 0 || bulkDisabled) return
    bulkAssign({
      rows: bulkRows.flatMap(row =>
        row.value
          ? [
              {
                provider: row.provider,
                providerKey: row.providerKey,
                value: row.value,
              },
            ]
          : []
      ),
    })
  }

  const renderRow = (row: RevenueSourceRow) => (
    <SourceRow
      key={row.id}
      row={row}
      cellClass={cellClass}
      canManage={canManage}
      disabled={isBulkLoading}
      onLoadingChange={handleRowLoadingChange}
    />
  )

  const headerClass = 'body-text-4 py-s px-m'
  const numericColumns: SortColumn[] = [
    'total_revenue',
    'percent_of_revenue',
    'transactions',
  ]
  const headerCell = (column: SortColumn, label: string, ariaLabel: string) => (
    <SortableColumnHeader
      label={label}
      sortAriaLabel={ariaLabel}
      direction={sortColumn === column ? sortDirection : null}
      onSort={() => handleSort(column)}
      className={headerClass}
      align={numericColumns.includes(column) ? 'right' : 'left'}
    />
  )

  return (
    <table
      ref={tableRef}
      tabIndex={-1}
      className={`w-100 ${css({ outline: 'none' })}`}
    >
      <thead>
        <tr>
          {headerCell(
            'revenue_source',
            t('revenue_source'),
            t('sort_by_revenue_source')
          )}
          {headerCell('pos', t('pos'), t('sort_by_pos'))}
          {headerCell('channel', t('channel'), t('sort_by_channel'))}
          {headerCell(
            'total_revenue',
            t('total_revenue'),
            t('sort_by_total_revenue')
          )}
          {headerCell(
            'percent_of_revenue',
            t('percent_of_revenue'),
            t('sort_by_percent_of_revenue')
          )}
          {headerCell(
            'transactions',
            t('transactions'),
            t('sort_by_transactions')
          )}
          <th scope="col" className={headerClass}>
            <span className="sr-only">{t('actions')}</span>
          </th>
        </tr>
      </thead>
      <tbody>
        {needsReview.length > 0 && (
          <SectionRow
            id="needs-review-section"
            label={t('needs_review_section', { count: needsReview.length })}
            highlighted
            headingRef={needsReviewHeadingRef}
            action={
              canManage &&
              confirmableRows.length > 0 && (
                <Button
                  kind="secondary"
                  size="small"
                  disabled={bulkDisabled}
                  isLoading={isBulkLoading}
                  onClick={handleBulkConfirm}
                >
                  {confirmableRows.length > BULK_ASSIGN_MAX_ROWS
                    ? t('confirm_some_suggested', {
                        count: BULK_ASSIGN_MAX_ROWS,
                        total: confirmableRows.length,
                      })
                    : t('confirm_all_suggested', {
                        count: confirmableRows.length,
                      })}
                </Button>
              )
            }
          />
        )}
        {needsReview.map(renderRow)}
        {bulkError && (
          <tr>
            <td colSpan={COLUMN_COUNT} className="px-m">
              <div role="alert">
                <Text.BodyText5 color="negative">
                  {getErrorMessage(bulkError, tErrors('bulk_assign'))}
                </Text.BodyText5>
              </div>
            </td>
          </tr>
        )}
        {mapped.length > 0 && (
          <SectionRow
            id="mapped-section"
            label={t('mapped_section', { count: mapped.length })}
          />
        )}
        {mapped.map(renderRow)}
        {noChannelRows.length > 0 && (
          <SectionRow id="no-channel-section" label={t('no_channel_data')} />
        )}
        {noChannelRows.map(renderRow)}
      </tbody>
    </table>
  )
}

export default RevenueSourcesTable
